import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Badge, Box, Collapse, Flex, Stack, Text, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { useMemo } from "react";

const COUNTRY_FLAGS = {
  // Africa
  ethiopia: "🇪🇹", kenya: "🇰🇪", rwanda: "🇷🇼", burundi: "🇧🇮", tanzania: "🇹🇿",
  uganda: "🇺🇬", malawi: "🇲🇼", zambia: "🇿🇲", zimbabwe: "🇿🇼", mozambique: "🇲🇿",
  cameroon: "🇨🇲", madagascar: "🇲🇬", nigeria: "🇳🇬", angola: "🇦🇴",
  congo: "🇨🇩", "democratic republic of congo": "🇨🇩", "dr congo": "🇨🇩",
  "côte d'ivoire": "🇨🇮", "ivory coast": "🇨🇮",
  // Americas
  brazil: "🇧🇷", colombia: "🇨🇴", peru: "🇵🇪", bolivia: "🇧🇴", ecuador: "🇪🇨",
  venezuela: "🇻🇪", guatemala: "🇬🇹", honduras: "🇭🇳", mexico: "🇲🇽", panama: "🇵🇦",
  "costa rica": "🇨🇷", "el salvador": "🇸🇻", nicaragua: "🇳🇮",
  jamaica: "🇯🇲", cuba: "🇨🇺", haiti: "🇭🇹", "dominican republic": "🇩🇴",
  "united states": "🇺🇸", usa: "🇺🇸",
  // Asia & Pacific
  indonesia: "🇮🇩", vietnam: "🇻🇳", india: "🇮🇳", myanmar: "🇲🇲", thailand: "🇹🇭",
  philippines: "🇵🇭", "papua new guinea": "🇵🇬", laos: "🇱🇦", china: "🇨🇳",
  "timor-leste": "🇹🇱", "timor leste": "🇹🇱", taiwan: "🇹🇼", nepal: "🇳🇵",
  // Middle East
  yemen: "🇾🇪",
};

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const countryFlag = (country) => {
  if (!country) return "";
  const key = country.toLowerCase().split(/[/,]/)[0].trim();
  return COUNTRY_FLAGS[key] ?? "";
};

// ─── Design tokens ─────────────────────────────────────────────────────────────
// Centralised here so ShopProfilePage and any future components stay in sync.
export function useTk() {
  return {
    surface: useColorModeValue("#fdfaf5", "#1e1510"),
    surface2: useColorModeValue("#dfc89a", "#252018"),
    border: useColorModeValue("rgba(0,0,0,0.1)", "rgba(255,255,255,0.07)"),
    cream: useColorModeValue("#1a0c04", "#e8d9c4"),
    creamDim: useColorModeValue("#6a4020", "#a89880"),
    accent: useColorModeValue("#8b3e12", "#c8a97e"),
    gold: useColorModeValue("#8b3e12", "#f0c040"),
    // Warm status — matches Explore cards; avoids default traffic-light green/red
    open: "#5a9e6f",
    closed: "#b07060",
    fadeBg: useColorModeValue("#f5ede0", "#1a1209"),
  };
}

// Process-specific colours — legible in both dark and light mode as coloured text on tinted bg
export const PROCESS_STYLE = {
  natural:   { bg: 'rgba(160,70,20,0.12)',  border: 'rgba(160,70,20,0.32)',  color: '#c86828' },
  washed:    { bg: 'rgba(30,110,160,0.10)', border: 'rgba(30,110,160,0.30)', color: '#2e88bb' },
  honey:     { bg: 'rgba(160,120,10,0.12)', border: 'rgba(160,120,10,0.32)', color: '#a88a10' },
  anaerobic: { bg: 'rgba(110,50,160,0.10)', border: 'rgba(110,50,160,0.30)', color: '#8850c8' },
};

// ─── Coffee entry card (menu item or retail bean) ──────────────────────────────
export function CoffeeEntryCard({ item, hideSource }) {
  const tk = useTk();
  const useBadgeBg      = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.07)");
  const highlightBg     = useColorModeValue("rgba(139,62,18,0.09)", "rgba(200,169,126,0.09)");
  const highlightBorder = useColorModeValue("rgba(139,62,18,0.24)", "rgba(200,169,126,0.24)");

  const VERIFICATION = {
    owner:     { label: "Owner verified", color: tk.accent,   bg: highlightBg,            border: highlightBorder },
    community: { label: "Community verified", color: tk.accent, bg: highlightBg,           border: highlightBorder },
    ai:        { label: "AI sourced",      color: tk.creamDim, bg: "transparent",          border: tk.border },
  };
  const verification = VERIFICATION[item.verified_by || item.source] ?? VERIFICATION.ai;
  const houseRoastBorder = useColorModeValue("#8b3e12", "#c8a97e");
  const stripDivider    = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.04)");

  // Roast word colors keyed by level — all three always called (Rules of Hooks)
  const ROAST_COLORS = {
    light:  useColorModeValue('#9a7828', '#c8a97e'),
    medium: useColorModeValue('#8b3e12', '#b87838'),
    dark:   useColorModeValue('#5a2010', '#a05030'),
  };
  const roastColor = ROAST_COLORS[item.roast_level] ?? tk.creamDim;

  const flag        = countryFlag(item.country);
  const processKey  = item.process?.toLowerCase();
  const processStyle = PROCESS_STYLE[processKey] || null;

  const showInCup    = item.use_type === 'menu'   || item.use_type === 'both';
  const showTakeHome = item.use_type === 'retail' || item.use_type === 'both';

  // ponytail: producer is captured and stored but not rendered anywhere yet.
  // Extracted values run to full farm addresses ("Elder Castro, Las Acacias, El
  // Cerrito, Huila") which wrap this line in a two-column card, and ~8% of them
  // repeat the region verbatim. Needs its own surface — an expanded card or a
  // provenance line — rather than being squeezed in here. Same for `body` when
  // it stops fitting the descriptor row.
  const detailLine = [
    item.region,
    item.variety,
    item.roaster_name ? `by ${item.roaster_name}` : null,
  ].filter(Boolean).join(" · ");

  const tastingLine = item.tasting_notes?.length
    ? item.tasting_notes.map(n => n.toLowerCase()).join(', ')
    : null;

  // Single inline descriptor, read left to right as how the coffee drinks:
  // what was done to it (process, roast) → how it lands (body, acidity) → how it's
  // served. Body and acidity stay dim on purpose — the process dot and roast word
  // already carry colour, and five coloured chips would read as noise.
  const descriptors = useMemo(() => [
    item.process     ? { label: cap(item.process), color: processStyle?.color || tk.creamDim, dot: processStyle?.color } : null,
    item.roast_level ? { label: `${cap(item.roast_level)} roast`, color: roastColor } : null,
    item.body        ? { label: `${cap(item.body)} body`, color: tk.creamDim } : null,
    item.acidity     ? { label: `${cap(item.acidity)} acidity`, color: tk.creamDim } : null,
    item.drink_type  ? { label: item.drink_type, color: tk.creamDim } : null,
  ].filter(Boolean), [item.process, item.roast_level, item.body, item.acidity, item.drink_type, processStyle, roastColor, tk.creamDim]);

  return (
    <Box
      bg={tk.surface}
      borderRadius="12px"
      border={`1px solid ${tk.border}`}
      borderLeft={`3px solid ${houseRoastBorder}`}
      overflow="hidden"
    >
      {/* Country strip + badges */}
      <Flex px={5} pt="14px" pb="12px" align="flex-start" justify="space-between" borderBottom={`1px solid ${stripDivider}`}>
        <Text fontSize="11px" fontWeight={600} letterSpacing="0.06em" color={tk.accent} textTransform="uppercase" flex={1} mr={2}>
          {flag ? <>{flag}&nbsp;{item.country}</> : (item.country || '')}
        </Text>
        <Flex gap="5px" align="center" flexShrink={0}>
          {showInCup && (
            <Box fontSize="9px" fontWeight={500} letterSpacing="0.10em" textTransform="uppercase"
              color={tk.creamDim} bg={useBadgeBg} border={`1px solid ${tk.border}`}
              px="6px" py="2px" borderRadius="20px">
              In cup
            </Box>
          )}
          {showTakeHome && (
            <Box fontSize="9px" fontWeight={500} letterSpacing="0.10em" textTransform="uppercase"
              color={tk.creamDim} bg={useBadgeBg} border={`1px solid ${tk.border}`}
              px="6px" py="2px" borderRadius="20px">
              Take home
            </Box>
          )}
        </Flex>
      </Flex>

      {/* Main content */}
      <Box px={5} pt="16px" pb="20px">
        {/* Bean name — hero */}
        <Text
          fontFamily="heading"
          fontSize="16px" fontStyle="italic"
          color={tk.cream} lineHeight="1.3"
          mb={detailLine ? "6px" : "12px"}
        >
          {item.name}
        </Text>

        {/* Origin details */}
        {detailLine && (
          <Text fontSize="11px" color={tk.creamDim} mb="11px" lineHeight="1.5">
            {detailLine}
          </Text>
        )}

        {/* Process · roast · drink type — single inline line */}
        {descriptors.length > 0 && (
          <Flex align="center" gap="10px" mb={tastingLine ? "10px" : 0} flexWrap="wrap">
            {descriptors.map((d, i) => (
              <Flex key={i} align="center" gap="5px">
                {d.dot && (
                  <Box w="5px" h="5px" borderRadius="full" bg={d.dot} flexShrink={0} />
                )}
                {i > 0 && !d.dot && (
                  <Text fontSize="10px" color={tk.creamDim} opacity={0.4} userSelect="none">·</Text>
                )}
                <Text fontSize="11px" fontWeight={500} color={d.color}>{d.label}</Text>
              </Flex>
            ))}
          </Flex>
        )}

        {/* Tasting notes — flowing prose, not tags */}
        {tastingLine && (
          <Text fontSize="11.5px" fontStyle="italic" color={tk.accent} lineHeight="1.5" opacity={0.9} mb="10px">
            {tastingLine}
          </Text>
        )}
        {!hideSource && (
          <Flex justify="flex-end">
            <Text fontSize="9px" fontWeight={500} letterSpacing="0.08em" textTransform="uppercase" color={verification.color} opacity={0.7}>
              {verification.label}
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
}

// ─── Attribute accordion ───────────────────────────────────────────────────────
// Extracted because it has its own open/close state — everything else is
// just Chakra primitives used directly in the page.
// tags: [{ key, label, confirmed }] — confirmed = AI identified, unconfirmed = greyed out
export function AttributeCategory({ title, emoji, tags, defaultOpen, hideSource, forceOpen }) {
  const tk = useTk();
  const badgeBg = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.06)");
  const unconfirmedBorder = useColorModeValue("rgba(0,0,0,0.18)", "rgba(255,255,255,0.18)");
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });
  const open = forceOpen || isOpen;

  const confirmedCount = tags.filter((t) => t.confirmed).length;

  return (
    <Box bg={tk.surface} borderRadius="12px" border={`1px solid ${tk.border}`} overflow="hidden">
      <Flex as={forceOpen ? "div" : "button"} type={forceOpen ? undefined : "button"} w="100%" aria-expanded={forceOpen ? undefined : open} align="center" justify="space-between" px={4} py={3} cursor={forceOpen ? "default" : "pointer"} userSelect="none" onClick={forceOpen ? undefined : onToggle}>
        <Flex align="center" gap={2} fontSize="13px" fontWeight={500} color={tk.cream}>
          <Text fontSize="15px">{emoji}</Text>
          <Text>{title}</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Badge bg={badgeBg} color={tk.creamDim} fontSize="11px" fontWeight={400} px={2} borderRadius="full">
            {confirmedCount} confirmed
          </Badge>
          {open ? <ChevronUpIcon color={tk.creamDim} /> : <ChevronDownIcon color={tk.creamDim} />}
        </Flex>
      </Flex>

      <Collapse in={open} animateOpacity>
        <Stack spacing={3} px={4} pb={4}>
          {tags.map((tag, i) => (
            <Flex key={i} align="center" justify="space-between" fontSize="12.5px">
              <Flex align="center" gap={2}>
                {tag.confirmed ? (
                  <Flex w="18px" h="18px" borderRadius="full" bg={tk.accent} align="center" justify="center" flexShrink={0}>
                    <CheckIcon w="8px" h="8px" color="white" />
                  </Flex>
                ) : (
                  <Box w="18px" h="18px" borderRadius="full" border={`1.5px solid ${unconfirmedBorder}`} flexShrink={0} />
                )}
                <Text color={tag.confirmed ? tk.cream : tk.creamDim} opacity={tag.confirmed ? 1 : 0.6}>{tag.label}</Text>
              </Flex>
              {tag.confirmed && !hideSource && (
                tag.ownerVerified
                  ? <Text fontSize="11px" color={tk.accent} fontWeight={500}>✦ Owner verified</Text>
                  : <Text fontSize="11px" color={tk.creamDim}>AI tagged</Text>
              )}
            </Flex>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
